// @flow
import React from 'react';
import { Link, withRouter } from 'react-router';
import classnames from 'classnames';
import CapacitorRipple from '../capacitor/Ripple';
import { FormattedMessage, injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { compose } from 'redux';
import IconLabel from '../util/IconLabel';
import TitleWithPopularTitle from '../common/TitleWithPopularTitle';
import Separated from '../util/Separated';

import styles from './Facet.css';
import * as qs from 'qs';
import { booleanMessages } from '../messages/FilterMessages';
import { commonMessages } from '../messages/Common';

declare var __CAPACITOR__: boolean;

type OwnProps = {
  type: string,
  facet: Object,
  pathname: string,
  query: Object,
  className?: string,
  onClickFacet?: Function,
  isBoolean: boolean,
  router: Object,
  isActive: boolean,
};

type Props = OwnProps & {
  intl: IntlShape,
};

function generateLinkTo(
  filterKey: string,
  id: number | string | boolean,
  pathname: string,
  query: Object,
  isActive: boolean
): string {
  const filterIds = query[filterKey];
  const filterArray = filterIds ? filterIds.split(',') : [];

  const alteredQuery = { ...query };
  if (isActive) {
    alteredQuery[filterKey] = filterArray.filter(filterId => filterId !== id.toString()).join(',');

    if (alteredQuery[filterKey] === '') {
      delete alteredQuery[filterKey];
    }
  } else {
    alteredQuery[filterKey] = [...filterArray, id].join(',');
  }

  return pathname + '?' + qs.stringify(alteredQuery);
}

const getContent = (type, facet) => {
  switch (type) {
    case 'works':
      return <TitleWithPopularTitle popularTitle={facet.popularTitle} title={facet.title} />;
    case 'composers':
    case 'conductors':
    case 'soloists':
      return (
        <Separated>
          {facet.surname}
          {facet.forename}
        </Separated>
      );
    case 'instruments':
    case 'genres':
    case 'epochs':
      return facet.title;
    case 'ensembles':
    case 'recordingTypes':
      return facet.name;
    case 'isExclusive':
      return <FormattedMessage {...booleanMessages.isExclusive} />;
    case 'isOriginalWork':
    case 'isCompleteWork':
      return (
        <FormattedMessage
          id="facets.type.hide"
          defaultMessage="Hide {facetType}"
          values={{ facetType: <FormattedMessage {...booleanMessages[type]} /> }}
        />
      );
    default:
      return null;
  }
};

function getFacetLabelText(type, facet, intl) {
  switch (type) {
    case 'works':
      return facet.popularTitle || facet.title || '';
    case 'composers':
    case 'conductors':
    case 'soloists':
      return `${facet.surname || ''} ${facet.forename || ''}`.trim();
    case 'instruments':
    case 'genres':
    case 'epochs':
      return facet.title || '';
    case 'ensembles':
    case 'recordingTypes':
      return facet.name || '';
    case 'isExclusive':
      return intl.formatMessage(booleanMessages.isExclusive);
    case 'isOriginalWork':
    case 'isCompleteWork':
      return intl.formatMessage(booleanMessages[type]);
    default:
      return '';
  }
}

const Facet = ({
  className,
  type,
  facet,
  pathname,
  query,
  onClickFacet,
  isBoolean,
  router,
  isActive,
  intl,
}: Props) => {
  const classNames = classnames(styles.facet, styles.preview, className);
  const id = isBoolean ? facet.value : facet.id;
  const linkTo = generateLinkTo(type, id, pathname, query, isActive);

  const onClick = event => {
    if (onClickFacet) {
      onClickFacet(event, id);
      return;
    }
    if (__CAPACITOR__) {
      event.preventDefault();
      router.replace(linkTo);
    }
  };

  const facetTitle = getFacetLabelText(type, facet, intl);

  return (
    <Link
      to={linkTo}
      className={classNames}
      onClick={onClick}
      data-test="facet.item"
      aria-label={
        isActive
          ? intl.formatMessage(commonMessages.deselect, { title: facetTitle })
          : intl.formatMessage(commonMessages.select, { title: facetTitle })
      }
    >
      <CapacitorRipple />
      <div className={styles.container}>{getContent(type, facet)}</div>
      {facet.hits && <span className={styles.count}>{facet.hits}</span>}
      {isActive && <IconLabel name="close" className={styles.remove} />}
    </Link>
  );
};

export default compose(injectIntl, withRouter)(Facet);
